import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router'

import * as sessionUserSelector from 'selectors/sessionUser'
import {
  useFetchReleaseNotesQuery,
  useUpdateReleaseNoteMutation,
  useRemoveReleaseNoteMutation,
} from 'api/releaseNotes'

import moment from 'moment-wrapper'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Header from 'components/Shared/Header/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import ReleasesListSidebar from '../ReleaseNotesPage/ReleasesListSidebar/Index'
import AddEditReleaseForm from '../ReleaseNotesPage/AddEditReleaseForm/Index'
import { RouteReleaseNotes, RouteReleaseNotesEdit } from 'routes'

const ReleaseNotesEditPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data, isFetching } = useFetchReleaseNotesQuery()
  const [updateReleaseNote, { isLoading: isUpdating }] = useUpdateReleaseNoteMutation()
  const [removeReleaseNote, { isLoading: isDeleting }] = useRemoveReleaseNoteMutation()

  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const canAddEdit = userIsAdmin

  const params = useParams()
  const selectedReleaseNoteId = params.releaseNoteId || null

  const releaseNotes = data?.releaseNotes || []
  const selectedReleaseNote = selectedReleaseNoteId ? releaseNotes.find(v => v.id === selectedReleaseNoteId) : null

  if (!canAddEdit) {
    navigate(RouteReleaseNotes({ releaseNoteId: selectedReleaseNoteId }))
    return
  }

  const onSubmit = (values) => {
    if (moment.isMoment(values.date)) {
      values.date = values.date.toISOString()
    }

    updateReleaseNote({ id: selectedReleaseNoteId, params: values }).then(fulfilled => {
      if (!fulfilled.data?.error) {
        navigate(RouteReleaseNotes({ releaseNoteId: selectedReleaseNoteId }))
      }
    })
  }

  const onSidebarReleaseNoteClick = (releaseNote) => {
    navigate(RouteReleaseNotesEdit({ releaseNoteId: releaseNote.id }))
  }

  const onCloseForm = () => {
    navigate(RouteReleaseNotes({ releaseNoteId: selectedReleaseNoteId }))
  }

  const removeRelease = () => {
    removeReleaseNote({ id: selectedReleaseNoteId }).then(fulfilled => {
      if (!fulfilled.data?.error) {
        navigate(RouteReleaseNotes())
      }
    })
  }

  const getBreadcrumbs = () => {
    const breadcrumbsList = [
      {
        text: t('common.breadcrumbReleaseNotes'),
        url: RouteReleaseNotes({ releaseNoteId: selectedReleaseNoteId }),
      },
      {
        text: t('common.breadcrumbReleaseNotesEdit', { releaseName: selectedReleaseNote?.version || '' }),
      },
    ]
    return breadcrumbsList
  }

  const title = selectedReleaseNote ? t('releaseNotesPage.headerEdit', { releaseName: selectedReleaseNote.version }) : ''
  const isLoading = isFetching || isUpdating || isDeleting

  return (
    <MainWrap pageTitle={title}>
      <ReleasesListSidebar
        releaseNotes={releaseNotes}
        selectedReleaseNoteId={selectedReleaseNoteId}
        onReleaseNoteClick={onSidebarReleaseNoteClick}
      />

      <Content loading={isLoading}>
        <Header
          title={title}
          breadcrumbsList={getBreadcrumbs()}
        />

        {selectedReleaseNote && (
          <AddEditReleaseForm
            onSubmit={onSubmit}
            onCloseClick={onCloseForm}
            onRemoveClick={removeRelease}
            editRelease={selectedReleaseNote}
          />
        )}
      </Content>
    </MainWrap>
  )
}

export default ReleaseNotesEditPage
